// ==================== 裁切 ====================

/** 裁切任务模式 */
export enum ClipTask {
  /** 不执行裁切 */
  NONE = 0,
  /** 高亮被裁切区域，不真正裁剪 */
  HIGHLIGHT = 1,
  /** 仅显示裁切体内部点 */
  SHOW_INSIDE = 2,
  /** 仅显示裁切体外部点 */
  SHOW_OUTSIDE = 3,
}

/** 裁切方法 */
export enum ClipMethod {
  /** 命中任一裁切体即视为在内 */
  INSIDE_ANY = 0,
  /** 需同时命中全部裁切体才视为在内 */
  INSIDE_ALL = 1,
}

// ==================== 点云渲染 ====================

/** 点大小类型 */
export enum PointSizeType {
  /** 固定像素尺寸，不随距离变化 */
  FIXED = 0,
  /** 按距离衰减的世界尺寸 */
  ATTENUATED = 1,
  /** 根据八叉树节点层级自适应的尺寸 */
  ADAPTIVE = 2,
}

/** 点形状 */
export enum PointShape {
  /** 方形点精灵 */
  SQUARE = 0,
  /** 圆形点精灵 */
  CIRCLE = 1,
  /** 抛物面点精灵 */
  PARABOLOID = 2,
}

/** 高程渐变重复模式 */
export const ElevationGradientRepeat = {
  CLAMP: 0,
  REPEAT: 1,
  MIRRORED_REPEAT: 2,
}

/** 树类型 */
export const TreeType = {
  OCTREE: 0,
  KDTREE: 1,
}

// ==================== 默认值常量 ====================

/** 默认点预算 */
export const DEFAULT_POINT_BUDGET = 2_000_000
/** 默认最大并发加载节点数 */
export const DEFAULT_MAX_NODES_LOADING = 4
/** 默认最小节点屏幕尺寸（像素） */
export const DEFAULT_MIN_NODE_SIZE = 30

/** 多边形裁切允许最大顶点数量 */
export const ClipPolygonMaxVerticeCount = 100

// ==================== 加载器默认值 ====================

/** 默认最大并发加载数 */
export const DEFAULT_MAX_CONCURRENT_LOADS = 4
/** 默认最大重试次数 */
export const DEFAULT_MAX_RETRIES = 2

// ==================== 拾取默认值 ====================

/** 默认最大拾取返回结果数 */
export const DEFAULT_MAX_PICK_RESULTS = 100
/** GPU 拾取缓冲默认尺寸 */
export const DEFAULT_PICK_BUFFER_SIZE = 512
/** GPU 拾取默认 Alpha 裁切阈值 */
export const DEFAULT_PICK_ALPHA_CLIP = 0.3
/** 拾取距离阈值 */
export const DEFAULT_PICK_THRESHOLD = 1.0
/** 中心点拾取默认点大小（像素） */
export const DEFAULT_CENTERS_PICK_POINT_SIZE = 2.0
/** 矩形拾取默认最大返回数 */
export const DEFAULT_RECT_PICK_MAX_RESULTS = 10000
/** 点云拾取窗口默认尺寸 */
/**
 * 默认拾取窗口（像素）。
 *
 * 拾取语义是"窗口内离光标最近的可见点"，因此窗口越大，返回点离光标越远：65px 时实测偏差可达 32px，
 * 表现为控制器/测量"点不准"。收紧到 15px 后最坏偏差约 7px，同时保留必要的容错；工具可显式覆盖
 * （例如测量 hover 用 3px、click 用 5px）。
 */
export const DEFAULT_PICK_WINDOW_SIZE = 15

// ==================== 相机默认值 ====================

/** 相机 position 与 target 最小允许距离 */
export const CAMERA_MIN_DISTANCE = 0.001
/** 近裁剪面相对于焦点距离的比例 */
export const CAMERA_NEAR_TARGET_RATIO = 0.01
/** 场景外近裁剪面相对于场景边界距离的比例 */
export const CAMERA_NEAR_BOUNDS_RATIO = 0.5
/** far 面额外保守留白比例 */
export const CAMERA_FAR_PADDING_RATIO = 0.25
/** 默认近裁剪面距离 */
export const CAMERA_DEFAULT_NEAR = 0.1
/** 默认远裁剪面距离 */
export const CAMERA_DEFAULT_FAR = 10000
/** 正交相机 userData 中保存透视等价垂直 FOV 的内部键 */
export const ORTHOGRAPHIC_EQUIVALENT_FOV_USER_DATA_KEY = 'share3dEquivalentPerspectiveFov'
/** 适配视角默认填充比例 */
export const CAMERA_FIT_PADDING = 1.2

// ==================== 控制器默认值 ====================

/** 轨道/地球模式阻尼因子 */
export const CONTROL_DAMPING_FACTOR = 0.1
/** 第一人称移动速度 */
export const FIRST_PERSON_MOVEMENT_SPEED = 10
/** 第一人称视角转动速度 */
export const FIRST_PERSON_LOOK_SPEED = 0.005

// ==================== GSplat 排序阈值 ====================

/** 排序位置变化阈值 */
export const GSPLAT_SORT_POSITION_THRESHOLD = 0.01
/** 排序方向变化阈值（点积差值） */
export const GSPLAT_SORT_DIRECTION_THRESHOLD = 0.0001
/** SH 方向计算阈值 */
export const GSPLAT_SH_DIRECTION_THRESHOLD = 1e-3
/** 直方图默认分箱数 */
export const GSPLAT_DEFAULT_BIN_COUNT = 256
/** 对数缩放偏移缩放因子 */
export const GSPLAT_LOG_SCALE_DIVISOR = 1_000_000

// ==================== 数值安全阈值 ====================

/** 通用浮点安全最小值（防除零） */
export const EPSILON = 1e-6
/** 高精度浮点安全最小值 */
export const EPSILON_HIGH = 1e-10
/** 超高精度浮点安全最小值 */
export const EPSILON_ULTRA = 1e-12

// ==================== 坐标工具默认值 ====================

/** 屏幕到场景坐标转换默认 Z 深度 */
export const DEFAULT_UNPROJECT_Z = 0.5

// ==================== 键盘码 ====================

export { KeyCodes } from './KeyCodes'
